import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Alert } from "../components/ui/alert";
import Navbar from "../components/ui/Navbar";
import ModalFullHV from "../components/modals/ModalFullHV";
import { toast } from "react-toastify";
import {
  FileText,
  CalendarDays,
  Package,
  Wrench,
  ShoppingCart,
  Tag,
  FileChartColumn,
} from "lucide-react";

import ModalReporteCronograma from "../components/modals/ModalReporteCronograma.jsx";
import ModalReporteServicios from "../components/modals/ModalReporteServicios.jsx";
import ModalReporteRepuestos from "../components/modals/ModalReporteRepuestos.jsx";
import ModalEtiquetasMasivas from "../components/modals/ModalEtiquetasMasivas.jsx";
import ModalReporteAlmacen from "../components/modals/ModalReporteAlmacen.jsx";
import ModalReporteCompras from "../components/modals/ModalReporteCompras.jsx";

const ReportesPage = () => {
  const [error, setError] = useState("");
  const [showModalCronograma, setShowModalCronograma] = useState(false);
  const [showModalServicios, setShowModalServicios] = useState(false);
  const [showModalRepuestos, setShowModalRepuestos] = useState(false);
  const [showModalEtiquetas, setShowModalEtiquetas] = useState(false);
  const [showModalAlmacen, setShowModalAlmacen] = useState(false);
  const [showModalCompras, setShowModalCompras] = useState(false);
  const [loadingReporte, setLoadingReporte] = useState(false);
  const [showModalFullHV, setShowModalFullHV] = useState(false);
  const [fullHVJobs, setFullHVJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;

  const cargarSolicitudesFullHV = async () => {
    try {
      setLoadingJobs(true);
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/reportes/full-hv/solicitudes`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("No se pudieron cargar las solicitudes");

      const data = await res.json();
      setFullHVJobs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingJobs(false);
    }
  };

  useEffect(() => {
    cargarSolicitudesFullHV();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hayPendientes = fullHVJobs.some((j) =>
      ["pendiente", "procesando"].includes(j.estado),
    );

    if (!hayPendientes) return undefined;

    const interval = setInterval(cargarSolicitudesFullHV, 6000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullHVJobs]);

  const descargarSolicitud = async (
    jobId,
    nombreArchivo = "reportes_completos.zip",
  ) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_URL}/reportes/full-hv/solicitudes/${jobId}/descargar`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!res.ok) throw new Error("No se pudo descargar el archivo");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nombreArchivo;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "No se pudo descargar el archivo");
    }
  };

  const handleGenerarReporte = (tipo) => {
    try {
      setError("");

      switch (tipo) {
        case "cronograma":
          setShowModalCronograma(true);
          break;
        case "servicios":
          setShowModalServicios(true);
          break;
        case "repuestos":
          setShowModalRepuestos(true);
          break;
        case "etiquetas":
          setShowModalEtiquetas(true);
          break;
        case "almacen":
          setShowModalAlmacen(true);
          break;
        case "compras":
          setShowModalCompras(true);
          break;
        case "fullhv":
          setShowModalFullHV(true);
          break;
        default:
          toast.warning("Reportes en proceso de ajuste");
          break;
      }
    } catch (err) {
      const msg = err.message || "Error al abrir el modal del reporte";
      setError(msg);
      toast.error(msg);
    }
  };

  const reportes = [
    {
      titulo: "Cronograma de Servicios",
      descripcion:
        "Genera el calendario de programación anual de mantenimientos y servicios técnicos.",
      icono: <CalendarDays className="w-8 h-8 text-blue-500" />,
      tipo: "cronograma",
    },
    {
      titulo: "Servicios",
      descripcion:
        "Descarga reportes detallados de servicios ejecutados, filtrados por cliente o área.",
      icono: <Wrench className="w-8 h-8 text-green-500" />,
      tipo: "servicios",
    },
    {
      titulo: "Almacén",
      descripcion:
        "Obtén un informe del inventario actual, incluyendo existencias, movimientos y ubicación.",
      icono: <Package className="w-8 h-8 text-amber-500" />,
      tipo: "almacen",
    },
    {
      titulo: "Repuestos",
      descripcion:
        "Consulta las salidas y consumos de repuestos asociados a mantenimientos realizados.",
      icono: <FileText className="w-8 h-8 text-purple-500" />,
      tipo: "repuestos",
    },
    {
      titulo: "Compras",
      descripcion:
        "Genera reportes de órdenes de compra, incluyendo estados, costos y proveedores.",
      icono: <ShoppingCart className="w-8 h-8 text-rose-500" />,
      tipo: "compras",
    },
    {
      titulo: "Etiquetas de inventario",
      descripcion:
        "Imprime etiquetas de inventario de equipos de manera masiva según filtros.",
      icono: <Tag className="w-8 h-8 text-cyan-500" />,
      tipo: "etiquetas",
    },
    {
      titulo: "Full hojas de vida por áreas o equipos",
      descripcion:
        "Descarga un ZIP con hojas de vida, servicios e historial por cada equipo.",
      icono: <FileText className="w-8 h-8 text-indigo-500" />,
      tipo: "fullhv",
    },
  ];

  const generarFullHV = async (filtros) => {
    try {
      setLoadingReporte(true);
      const token = localStorage.getItem("token");

      const res = await fetch(`${API_URL}/reportes/full-hv/solicitar`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(filtros),
      });

      if (!res.ok) throw new Error("No se pudo crear la solicitud");

      toast.success(
        "Solicitud enviada. Te notificaremos al estar lista (disponible por 24 horas).",
      );
      await cargarSolicitudesFullHV();
    } catch (err) {
      toast.error(err.message || "Error generando el reporte");
    } finally {
      setLoadingReporte(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="container mx-auto px-4 py-6 pt-20 md:pt-28">
        <Card className="shadow-md">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-600 rounded-xl">
                <FileChartColumn className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Reportes</h1>
                <p className="text-sm text-gray-500">
                  Generar y descargar reportes
                </p>
              </div>
            </div>

            {error && (
              <Alert variant="destructive" className="mb-4">
                <p>{error}</p>
              </Alert>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {reportes.map((r) => (
                <div
                  key={r.tipo}
                  className="border rounded-lg p-6 bg-white hover:shadow-lg transition-shadow flex flex-col justify-between"
                >
                  <div className="flex items-center mb-4">
                    {r.icono}
                    <h3 className="ml-3 text-lg font-semibold text-gray-800">
                      {r.titulo}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">{r.descripcion}</p>
                  <Button
                    onClick={() => handleGenerarReporte(r.tipo)}
                    className="mt-auto"
                  >
                    Generar Reporte
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-md mt-6">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Solicitudes Full Hojas de Vida
              </h3>
              <Button variant="outline" onClick={cargarSolicitudesFullHV}>
                Actualizar
              </Button>
            </div>

            {loadingJobs ? (
              <p className="text-sm text-gray-500">Cargando solicitudes...</p>
            ) : fullHVJobs.length === 0 ? (
              <p className="text-sm text-gray-500">Aún no hay solicitudes.</p>
            ) : (
              <div className="space-y-3">
                {fullHVJobs.map((job) => (
                  <div
                    key={job._id}
                    className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        Solicitud #{job._id.slice(-6)}
                      </p>
                      <p className="text-xs text-gray-500">
                        Estado:{" "}
                        <span className="font-semibold">{job.estado}</span> ·
                        Solicitado: {new Date(job.createdAt).toLocaleString()}
                      </p>
                      {job.error && (
                        <p className="text-xs text-red-600 mt-1">{job.error}</p>
                      )}
                      {job.expiraEn && job.estado === "completado" && (
                        <p className="text-xs text-amber-600 mt-1">
                          Disponible hasta:{" "}
                          {new Date(job.expiraEn).toLocaleString()}
                        </p>
                      )}
                    </div>

                    {job.estado === "completado" ? (
                      <Button
                        onClick={() =>
                          descargarSolicitud(
                            job._id,
                            job.nombreArchivo || "reportes_completos.zip",
                          )
                        }
                      >
                        Descargar ZIP
                      </Button>
                    ) : (
                      <span className="text-xs text-gray-500">
                        Se procesa en segundo plano...
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {showModalCronograma && (
          <ModalReporteCronograma
            onClose={() => setShowModalCronograma(false)}
          />
        )}

        {showModalServicios && (
          <ModalReporteServicios onClose={() => setShowModalServicios(false)} />
        )}

        {showModalRepuestos && (
          <ModalReporteRepuestos onClose={() => setShowModalRepuestos(false)} />
        )}

        {showModalEtiquetas && (
          <ModalEtiquetasMasivas onClose={() => setShowModalEtiquetas(false)} />
        )}

        {showModalAlmacen && (
          <ModalReporteAlmacen onClose={() => setShowModalAlmacen(false)} />
        )}

        {showModalCompras && (
          <ModalReporteCompras onClose={() => setShowModalCompras(false)} />
        )}

        {showModalFullHV && (
          <ModalFullHV
            onClose={() => setShowModalFullHV(false)}
            onGenerate={generarFullHV}
          />
        )}

        {/* Overlay de carga */}
        {loadingReporte && (
          <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm z-50 flex flex-col justify-center items-center text-white">
            <div className="animate-spin h-14 w-14 border-4 border-white border-t-transparent rounded-full"></div>
            <p className="mt-4 text-lg font-semibold">
              Generando reporte… por favor espera ⏳
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportesPage;
